import { omit } from 'ramda'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import { SchemaCampaign } from '@/__generated/es-api'
import { ERROR_GET_CAMPAIGN_ITEM_SERVER, TEMPLATES_TO_TYPE } from '@/app/module/campaigns/definitions'
import { transformReminderDTOToItem } from '@/app/module/campaigns/models/reminder/transform'
import { BackendCampaignTemplate, CampaignItem, UIStore } from '@/app/module/campaigns/types'
import { normalizeCampaignParts } from '@/app/module/campaigns/utils/campaign-content'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { FilesType } from '@/app/types'

export const useGetCampaign = (campaignId?: number | string) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const orgId = useSelector(selectOrgId)
  const query = esApi.endpoints.getCampaign.useQuery(
    { orgId: orgId.toString(), campaignId: String(campaignId) },
    { skip: !campaignId },
  )

  const { data: campaign, error } = query
  useEffect(() => {
    if (!error) {
      return
    }
    dispatch(
      createNotification({
        'get-campaign-failure': {
          type: 'error',
          message: getErrorMessage(error, ERROR_GET_CAMPAIGN_ITEM_SERVER),
        },
      }),
    )
    if (error && 'status' in error && error.status === 404) {
      navigate('/campaigns')
    }
  }, [error, dispatch, navigate])

  const transformedCampaign = useMemo(() => {
    if (!campaign) {
      return campaign
    }

    switch (campaign.template) {
      case 'reminder':
        return transformReminderDTOToItem(campaign) || {}
      default:
        return normalizeCampaignParts({
          ...omit(['template'], campaign),
          type: TEMPLATES_TO_TYPE[campaign.template as BackendCampaignTemplate] || campaign.template,
        } as CampaignItem)
    }
  }, [campaign]) as CampaignItem | undefined

  const files = useMemo(
    () => (query.currentData ? mergeCampaignFiles(query.currentData) : undefined),
    [query.currentData],
  )

  return {
    ...query,
    files,
    data: transformedCampaign,
  }
}

/**
 * @description Merge campaign files and uiStore files into a single object to use in the UI.
 * This is to ensure that both files from campaign object and files from uiStore are available in the UI.
 */
export const mergeCampaignFiles = (camp: SchemaCampaign) => {
  const { files = {} } = camp
  const uiStore = (camp.uiStore || {}) as UIStore
  const { files: uiStoreFiles = {} } = uiStore

  return {
    ...uiStoreFiles,
    ...files, // campaign files take precedence over uiStore files
  } as FilesType
}
